import classNames from 'classnames'
import React, { useState } from 'react'
import {
  AFFILIATE_POLICY_URL,
  BUSINESS_TYPE_TIPS,
  BUSINESS_TYPES,
  BUSINESS_USE_CASES,
  isValidWebUrl,
} from './content'
import { styles } from './styles'
import Anchor from '../anchor'

interface FlowProps {
  submitting: boolean
  error: string
  onBack: () => void
  onSubmit: (
    answers: Record<string, any>,
    consents: Record<string, boolean>
  ) => void
}

const BusinessFlow = ({ submitting, error, onBack, onSubmit }: FlowProps) => {
  const classes = styles()
  const [step, setStep] = useState(1)
  const [name, setName] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [website, setWebsite] = useState('')
  const [social, setSocial] = useState('')
  const [businessType, setBusinessType] = useState<string | null>(null)
  const [businessTypeOther, setBusinessTypeOther] = useState('')
  const [useCases, setUseCases] = useState<Record<string, boolean>>({})
  const [useCaseOther, setUseCaseOther] = useState('')
  const [description, setDescription] = useState('')
  const [consents, setConsents] = useState<Record<string, boolean>>({})
  const [localError, setLocalError] = useState('')

  const goTo = (n: number) => {
    setLocalError('')
    setStep(n)
  }

  const next = () => {
    setLocalError('')
    if (step === 1) {
      if (!name.trim() || !city.trim() || !country.trim()) {
        setLocalError('Please fill in your business name, city, and country.')
        return
      }
      if (website.trim().length < 4 && social.trim().length < 4) {
        setLocalError('A website or social handle is required to proceed.')
        return
      }
      if (website.trim() && !isValidWebUrl(website)) {
        setLocalError('Please enter a valid website URL.')
        return
      }
    }
    if (step === 2) {
      if (!businessType) {
        setLocalError('Please select your type of business.')
        return
      }
      if (businessType === 'other' && !businessTypeOther.trim()) {
        setLocalError('Please briefly describe your type of business.')
        return
      }
      if (!Object.keys(useCases).some(k => useCases[k])) {
        setLocalError('Please select at least one use case.')
        return
      }
      if (useCases.other && !useCaseOther.trim()) {
        setLocalError('Please describe how you will use the share links.')
        return
      }
    }
    if (step === 3 && description.trim().length < 20) {
      setLocalError('Please write a description of at least 20 characters.')
      return
    }
    setStep(step + 1)
  }

  const generate = () => {
    if (!consents.terms || !consents.accurate) {
      setLocalError('Please check both boxes to continue.')
      return
    }
    setLocalError('')
    onSubmit(
      {
        name: name.trim(),
        city: city.trim(),
        country: country.trim(),
        website: website.trim(),
        social: social.trim(),
        businessType,
        businessTypeOther: businessTypeOther.trim(),
        useCases: Object.keys(useCases).filter(k => useCases[k]),
        useCaseOther: useCaseOther.trim(),
        description: description.trim(),
      },
      { terms: !!consents.terms, accurate: !!consents.accurate }
    )
  }

  return (
    <div>
      <div className={classes.pips} style={{ marginBottom: 14 }}>
        {[1, 2, 3, 4].map(n => (
          <span
            key={n}
            className={classNames(classes.pip, { [classes.pipOn]: n <= step })}
          />
        ))}
      </div>

      {step === 1 && (
        <div>
          <p className={classes.intro}>
            Tell us about your business so we can set up your share link
            correctly.
          </p>
          <label className={classes.label}>Business name *</label>
          <input
            className={classes.input}
            placeholder="e.g. Bloom Interiors"
            value={name}
            onChange={e => setName(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <div style={{ display: 'flex', gap: 9, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label className={classes.label}>City *</label>
              <input
                className={classes.input}
                placeholder="e.g. Austin"
                value={city}
                onChange={e => setCity(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className={classes.label}>Country *</label>
              <input
                className={classes.input}
                placeholder="e.g. United States"
                value={country}
                onChange={e => setCountry(e.target.value)}
              />
            </div>
          </div>
          <label className={classes.label}>Online presence *</label>
          <p className={classes.hint}>
            A website or social handle is required to proceed.
          </p>
          <input
            className={classes.input}
            placeholder="https://yourbusiness.com"
            value={website}
            onChange={e => setWebsite(e.target.value)}
            style={{ marginBottom: 7 }}
          />
          <input
            className={classes.input}
            placeholder="@handle or profile URL"
            value={social}
            onChange={e => setSocial(e.target.value)}
          />
          {(localError || error) && (
            <p className={classes.error}>{localError || error}</p>
          )}
          <div className={classes.btnRow}>
            <button type="button" className={classes.backBtn} onClick={onBack}>
              Back
            </button>
            <button
              type="button"
              className={classNames(classes.primaryBtn, classes.rowPrimaryBtn)}
              onClick={next}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <p className={classes.intro}>
            Help us understand your business and how you plan to use share
            links.
          </p>
          <label className={classes.label}>
            What type of business are you?
          </label>
          <div className={classes.tileGrid}>
            {BUSINESS_TYPES.map(bt => (
              <div
                key={bt.id}
                className={classNames(classes.tile, {
                  [classes.tileOn]: businessType === bt.id,
                  [classes.tileWide]: bt.id === 'other',
                })}
                onClick={() => setBusinessType(bt.id)}
              >
                {bt.label}
              </div>
            ))}
          </div>
          {businessType === 'other' && (
            <input
              className={classes.input}
              placeholder="Briefly describe your type of business..."
              value={businessTypeOther}
              onChange={e => setBusinessTypeOther(e.target.value)}
              style={{ marginBottom: 12 }}
            />
          )}
          <label className={classes.label}>
            How will you use links? (select all that apply)
          </label>
          {BUSINESS_USE_CASES.map(uc => (
            <label key={uc.id} className={classes.checkRow}>
              <input
                type="checkbox"
                checked={!!useCases[uc.id]}
                onChange={e =>
                  setUseCases(prev => ({ ...prev, [uc.id]: e.target.checked }))
                }
              />
              <span>{uc.label}</span>
            </label>
          ))}
          {useCases.other && (
            <input
              className={classes.input}
              placeholder="Briefly describe how you'll use share links..."
              value={useCaseOther}
              onChange={e => setUseCaseOther(e.target.value)}
            />
          )}
          {(localError || error) && (
            <p className={classes.error}>{localError || error}</p>
          )}
          <div className={classes.btnRow}>
            <button
              type="button"
              className={classes.backBtn}
              onClick={() => goTo(1)}
            >
              Back
            </button>
            <button
              type="button"
              className={classNames(classes.primaryBtn, classes.rowPrimaryBtn)}
              onClick={next}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <p className={classes.intro}>
            Describe in your own words how you plan to use Chirpyest share
            links.
          </p>
          {businessType && (
            <div className={classes.summary}>
              Tip:{' '}
              {BUSINESS_TYPE_TIPS[businessType] || BUSINESS_TYPE_TIPS.other}
            </div>
          )}
          <label className={classes.label}>Your description *</label>
          <textarea
            className={classes.textarea}
            rows={4}
            placeholder="Write a brief description..."
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          <p className={classes.smallNote}>Minimum 20 characters.</p>
          {(localError || error) && (
            <p className={classes.error}>{localError || error}</p>
          )}
          <div className={classes.btnRow}>
            <button
              type="button"
              className={classes.backBtn}
              onClick={() => goTo(2)}
            >
              Back
            </button>
            <button
              type="button"
              className={classNames(classes.primaryBtn, classes.rowPrimaryBtn)}
              onClick={next}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div>
          <p className={classes.intro}>
            Review your details and confirm you've read our affiliate policy.
          </p>
          <div className={classes.summary}>
            <div>
              <strong>{name}</strong>, {city}, {country}
            </div>
            {website && <div>Website: {website}</div>}
            {social && <div>Social: {social}</div>}
            <div>
              Type:{' '}
              {businessType === 'other'
                ? `Other: ${businessTypeOther}`
                : BUSINESS_TYPES.find(b => b.id === businessType)?.label}
            </div>
            <div style={{ marginTop: 6 }}>{description}</div>
          </div>
          <div className={classes.summary} style={{ marginBottom: '1rem' }}>
            <p style={{ margin: '0 0 6px' }}>
              By generating this link you agree to Chirpyest's affiliate terms
              and confirm your information is accurate.
            </p>
            <Anchor
              target="_blank"
              href={AFFILIATE_POLICY_URL}
              style={{ fontSize: 12, color: '#185FA5' }}
            >
              Read the full affiliate policy
            </Anchor>
          </div>
          <label className={classes.consentRow}>
            <input
              type="checkbox"
              checked={!!consents.terms}
              onChange={e =>
                setConsents(prev => ({ ...prev, terms: e.target.checked }))
              }
            />
            <span>
              I have read the Chirpyest affiliate policy and agree to all terms.
            </span>
          </label>
          <label className={classes.consentRow}>
            <input
              type="checkbox"
              checked={!!consents.accurate}
              onChange={e =>
                setConsents(prev => ({ ...prev, accurate: e.target.checked }))
              }
            />
            <span>
              I confirm that the business information and intended use I've
              provided is accurate.
            </span>
          </label>
          {(localError || error) && (
            <p className={classes.error}>{localError || error}</p>
          )}
          <div className={classes.btnRow}>
            <button
              type="button"
              className={classes.backBtn}
              onClick={() => goTo(3)}
            >
              Back
            </button>
            <button
              type="button"
              className={classNames(classes.primaryBtn, classes.rowPrimaryBtn)}
              disabled={submitting}
              onClick={generate}
            >
              {submitting ? 'Generating' : 'Generate share link'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default BusinessFlow
